import { useRef, useState, useCallback, useEffect } from 'react';

// Ana grafik üzerinde basit çizim araçları (Trend Çizgisi, Fiyat Aralığı, Yatay/Dikey Çizgi) —
// lightweight-charts'ın native bir çizim seti yok, bu yüzden chart container'ının ÜSTÜNE şeffaf
// bir <canvas> overlay konup Canvas2D ile çizilir. Koordinat dönüşümü zaten bu dosyanın kullandığı
// Chart.jsx'teki TP/SL sürükleme tutamaçlarıyla AYNI API'yi (priceToCoordinate/timeToCoordinate)
// kullanır — ayrı bir "primitive/plugin" API'sine gerek yok.
//
// Çizimler SUNUCUYA kaydedilmez — bu bir işlem/pozisyon verisi değil, kişisel analiz notu — sembole
// özel `localStorage` anahtarıyla (ts_drawings_${symbol}) sadece bu tarayıcıda saklanır.

const POINTS_NEEDED = { hline: 1, vline: 1, trend: 2, range: 2 };
const STORAGE_PREFIX = 'ts_drawings_';

function loadDrawings(symbol) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + symbol);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export default function useChartDrawings(symbol) {
  const [drawings,   setDrawings]   = useState(() => loadDrawings(symbol));
  const [activeTool, setActiveTool] = useState(null); // null | 'trend' | 'hline' | 'vline' | 'range'

  const drawingsRef   = useRef(drawings);   drawingsRef.current   = drawings;
  const activeToolRef = useRef(activeTool); activeToolRef.current = activeTool;
  const pendingRef     = useRef([]); // biriken tıklama noktaları — araç yeterli nokta sayısına ulaşınca sıfırlanır

  const chartRef  = useRef(null);
  const seriesRef = useRef(null);
  const canvasRef = useRef(null);

  // Sembol değişince o sembolün kayıtlı çizimlerini yükle (pending noktaları da sıfırla — yarım
  // kalmış bir çizim başka bir sembole taşınmasın).
  useEffect(() => {
    pendingRef.current = [];
    setActiveTool(null);
    setDrawings(loadDrawings(symbol));
  }, [symbol]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_PREFIX + symbol, JSON.stringify(drawings)); } catch { /* quota vb. — sessizce yoksay */ }
  }, [symbol, drawings]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current, chart = chartRef.current, series = seriesRef.current;
    if (!canvas || !chart || !series) return;
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    ctx.clearRect(0, 0, width, height);

    for (const d of drawingsRef.current) {
      const pts = d.points.map(p => ({
        x: chart.timeScale().timeToCoordinate(p.time),
        y: series.priceToCoordinate(p.price),
        price: p.price,
      }));
      if (pts.some(p => p.x == null || p.y == null)) continue; // görünür aralığın dışında

      if (d.tool === 'trend') {
        ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y); ctx.lineTo(pts[1].x, pts[1].y); ctx.stroke();
      } else if (d.tool === 'hline') {
        ctx.strokeStyle = '#3b82f6'; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(0, pts[0].y); ctx.lineTo(width, pts[0].y); ctx.stroke();
        ctx.setLineDash([]);
      } else if (d.tool === 'vline') {
        ctx.strokeStyle = '#a161d2'; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(pts[0].x, 0); ctx.lineTo(pts[0].x, height); ctx.stroke();
        ctx.setLineDash([]);
      } else if (d.tool === 'range') {
        const x = Math.min(pts[0].x, pts[1].x), y = Math.min(pts[0].y, pts[1].y);
        const w = Math.abs(pts[1].x - pts[0].x), h = Math.abs(pts[1].y - pts[0].y);
        const diff = pts[1].price - pts[0].price;
        const pct  = pts[0].price !== 0 ? (diff / pts[0].price) * 100 : 0;
        const up   = diff >= 0;
        ctx.fillStyle   = up ? 'rgba(22,163,74,0.10)' : 'rgba(220,38,38,0.10)';
        ctx.strokeStyle = up ? '#16a34a' : '#dc2626';
        ctx.lineWidth = 1;
        ctx.fillRect(x, y, w, h);
        ctx.strokeRect(x, y, w, h);
        ctx.font = '11px sans-serif';
        ctx.fillStyle = up ? '#16a34a' : '#dc2626';
        const label = `${up ? '+' : ''}${diff.toFixed(2)} (${up ? '+' : ''}${pct.toFixed(2)}%)`;
        ctx.fillText(label, x + 4, y > 14 ? y - 4 : y + h + 14);
      }
    }
  }, []);

  useEffect(() => { redraw(); }, [drawings, redraw]);

  // chart.subscribeClick zaten Chart.jsx'in mevcut crosshair/pan davranışını hiç etkilemez — sadece
  // EK bir dinleyicidir. Araç aktif değilken tıklamalar sessizce yoksayılır.
  const handleClick = useCallback((param) => {
    const tool = activeToolRef.current;
    if (!tool || !param.time || !param.point || !seriesRef.current) return;
    const price = seriesRef.current.coordinateToPrice(param.point.y);
    if (price == null) return;
    pendingRef.current.push({ time: param.time, price });
    if (pendingRef.current.length >= POINTS_NEEDED[tool]) {
      const newDrawing = { id: `${Date.now()}_${Math.random()}`, tool, points: pendingRef.current };
      pendingRef.current = [];
      setDrawings(prev => [...prev, newDrawing]);
      setActiveTool(null);
    }
  }, []);

  // Chart oluşturulduğunda (createChart'tan hemen sonra) bir kere çağrılır. Dönen fonksiyon,
  // Chart.jsx'in kendi useEffect cleanup'ında çağrılmalı.
  const attachTo = useCallback((chart, series, canvas) => {
    chartRef.current  = chart;
    seriesRef.current = series;
    canvasRef.current = canvas;
    chart.subscribeClick(handleClick);
    chart.timeScale().subscribeVisibleLogicalRangeChange(redraw);
    redraw();
    return () => {
      chart.unsubscribeClick(handleClick);
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(redraw);
    };
  }, [handleClick, redraw]);

  // Pencere/konteyner yeniden boyutlanınca Chart.jsx'in mevcut handleResize'ından çağrılır — canvas'ın
  // piksel boyutu (width/height ATTRIBUTE'ları, CSS'i değil) container ile eşleşmezse çizim kayar.
  const resizeCanvas = useCallback((width, height) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width  = width;
    canvas.height = height;
    redraw();
  }, [redraw]);

  const clearAll = useCallback(() => {
    pendingRef.current = [];
    setActiveTool(null);
    setDrawings([]);
  }, []);

  return { drawings, activeTool, setActiveTool, clearAll, attachTo, resizeCanvas, canvasRef };
}
